/**
 * Mod-only "Add to scam photo blacklist" lightbox modal.
 *
 * Triggered from the photo lightbox menu ("Add to scam photo blacklist").
 * Renders a required-`mod_note` dialog and POSTs to
 * `/v1/mod/photos/{id}/ban` via ApiClient. Renders the dialog HTML
 * imperatively via `innerHTML` on a host element the modal owns, and only
 * ever writes user-controlled text through `textarea.value` / `textContent`,
 * never through `innerHTML` (see backend/CLAUDE.md "Don't use x-html for
 * AJAX modal/panel content").
 */
(function () {
    'use strict';

    var modalEl = null;
    var state = {
        photoId: '',
        submitting: false,
        priorBodyOverflow: null,
    };

    function ensureModal() {
        if (modalEl) return modalEl;
        modalEl = document.createElement('div');
        modalEl.id = 'photo-ban-lightbox-modal';
        modalEl.className = 'hidden fixed inset-0 z-[10010]';
        // Tailwind utility classes inline so the modal picks up dark-mode
        // pairs from the surrounding theme (the lightbox itself supports
        // dark mode). Standalone vanilla JS, not Alpine — no x-data, no
        // x-html. Mirrors the inspector remove modal layout.
        modalEl.innerHTML = ''
            + '<div class="photo-ban-backdrop fixed inset-0 bg-black/50 flex items-end justify-center sm:items-center sm:p-6">'
            + '  <div class="photo-ban-card bg-white dark:bg-gray-900 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[95dvh] overflow-hidden shadow-2xl">'
            + '    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">'
            + '      <h2 class="m-0 text-sm font-semibold text-gray-900 dark:text-gray-100">Add to scam photo blacklist</h2>'
            + '      <button type="button" class="photo-ban-close p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 text-lg leading-none border-0 bg-transparent cursor-pointer" aria-label="Close">&times;</button>'
            + '    </div>'
            + '    <div class="p-4 flex flex-col gap-3">'
            + '      <p class="m-0 text-xs text-gray-600 dark:text-gray-300">'
            + '        Pinning this photo to the scam-photo blacklist surfaces it on the inspector page and survives the source user being restored. The mod note is required for auditability.'
            + '      </p>'
            + '      <label class="flex flex-col gap-1">'
            + '        <span class="text-xs font-semibold text-gray-700 dark:text-gray-300">Mod note (required, 255 chars max)</span>'
            + '        <textarea class="photo-ban-note w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-fuchsia-500" rows="3" maxlength="255"'
            + '                  placeholder="e.g. known catfish stock photo, recurring scammer"></textarea>'
            + '      </label>'
            + '      <p class="photo-ban-error hidden m-0 text-xs text-red-600 dark:text-red-400"></p>'
            + '    </div>'
            + '    <div class="flex justify-end gap-2 px-4 py-3 border-t border-gray-100 dark:border-gray-800">'
            + '      <button type="button" class="photo-ban-cancel inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">Cancel</button>'
            + '      <button type="button" class="photo-ban-submit inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-white bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 cursor-pointer">Add to blacklist</button>'
            + '    </div>'
            + '  </div>'
            + '</div>';

        document.body.appendChild(modalEl);

        var backdrop = modalEl.querySelector('.photo-ban-backdrop');
        var card = modalEl.querySelector('.photo-ban-card');
        var closeBtn = modalEl.querySelector('.photo-ban-close');
        var cancelBtn = modalEl.querySelector('.photo-ban-cancel');
        var submitBtn = modalEl.querySelector('.photo-ban-submit');
        var noteEl = modalEl.querySelector('.photo-ban-note');

        backdrop.addEventListener('click', function (e) {
            if (e.target === backdrop) close();
        });
        card.addEventListener('click', function (e) { e.stopPropagation(); });
        closeBtn.addEventListener('click', close);
        cancelBtn.addEventListener('click', close);
        submitBtn.addEventListener('click', function () { submit(noteEl.value); });
        document.addEventListener('keydown', function (e) {
            if (!modalEl.classList.contains('hidden') && e.key === 'Escape') close();
        });

        return modalEl;
    }

    function showError(msg) {
        var el = modalEl.querySelector('.photo-ban-error');
        el.textContent = msg || '';
        el.classList.toggle('hidden', !msg);
    }

    function setNoteValue(val) {
        // textarea.value — never innerHTML. The echoed_reason from the
        // server may contain user-supplied content, so HTML injection via
        // innerHTML would be an XSS sink. value is treated as plain text.
        modalEl.querySelector('.photo-ban-note').value = val == null ? '' : String(val);
    }

    function open(photoId) {
        ensureModal();
        state.photoId = String(photoId || '');
        state.submitting = false;
        setNoteValue('');
        showError('');
        modalEl.classList.remove('hidden');
        // Lock body scroll while the modal is open. The lightbox itself
        // already locked scroll, but this modal renders on top — restore the
        // prior overflow value on close so we do not stomp the lightbox's
        // own lock.
        if (typeof document !== 'undefined' && document.body) {
            state.priorBodyOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        }
        // requestAnimationFrame instead of a fixed timeout. Focuses on the
        // next paint frame, which guarantees the element is visible.
        var noteEl = modalEl.querySelector('.photo-ban-note');
        if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(function () { noteEl.focus(); });
        } else {
            noteEl.focus();
        }
    }

    function close() {
        if (!modalEl) return;
        modalEl.classList.add('hidden');
        state.photoId = '';
        state.submitting = false;
        if (typeof document !== 'undefined' && document.body) {
            document.body.style.overflow = state.priorBodyOverflow || '';
            state.priorBodyOverflow = null;
        }
    }

    function submit(rawNote) {
        if (state.submitting) return;
        if (!window.ApiClient) {
            showError('ApiClient unavailable.');
            return;
        }
        var note = (rawNote || '').trim();
        if (note === '') {
            showError('Mod note is required.');
            return;
        }
        showError('');
        state.submitting = true;
        var submitBtn = modalEl.querySelector('.photo-ban-submit');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding…';

        window.ApiClient.request(
            '/mod/photos/' + encodeURIComponent(state.photoId) + '/ban',
            { method: 'POST', body: JSON.stringify({ mod_note: note }) }
        ).then(function (res) {
            if (res && typeof res.json === 'function') {
                return res.json().then(function (body) { return { ok: res.ok, status: res.status, body: body }; });
            }
            return { ok: true, status: 200, body: res };
        }).then(function (envelope) {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Add to blacklist';
            state.submitting = false;

            if (envelope.ok) {
                if (envelope.body && envelope.body.banned === false) {
                    var existingRow = envelope.body.existing_row;
                    var sourceLabel = existingRow && existingRow.ban_source
                        ? ' (' + String(existingRow.ban_source).replace(/_/g, ' ') + ')'
                        : '';
                    showError('Already in ban list' + sourceLabel + '.');
                    return;
                }
                close();
                if (window.toast && typeof window.toast.success === 'function') {
                    window.toast.success('Photo added to scam blacklist.');
                }
                return;
            }

            showError((envelope.body && envelope.body.error) ? envelope.body.error : 'Request failed.');
        }).catch(function (err) {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Add to blacklist';
            state.submitting = false;
            showError((err && err.message) ? err.message : 'Network error.');
        });
    }

    window.openPhotoBanLightboxModal = open;
})();
